import { api, unwrap } from './api';

/**
 * Cinch AI chat history (backendsafe `/ai/threads`, CINCHAI-105).
 *
 * The app creates and lists threads here; the mcp-proxy appends each finished turn with the
 * same user token, so the app never writes messages itself.
 */

export const listAiThreads = (params, options = {}) => api.get('/ai/threads', {
  params,
  ...options
}).then(unwrap);
export const getAiThread = id => api.get(`/ai/threads/${id}`).then(unwrap);
export const createAiThread = ({
  company,
  scope = 'company',
  folder = null
}) => api.post('/ai/threads', {
  company,
  scope,
  folder
}).then(unwrap);
export const updateAiThread = (id, patch) => api.patch(`/ai/threads/${id}`, patch).then(unwrap);
export const deleteAiThread = id => api.delete(`/ai/threads/${id}`);
export const getAiThreadMessages = id => api.get(`/ai/threads/${id}/messages`).then(unwrap);

/** Cinch AI memories (`/ai/memories`, CINCHAI-111/112): personal and company stores. */
export const listAiMemories = params => api.get('/ai/memories', {
  params: {
    limit: 500,
    ...params
  }
}).then(unwrap);
export const createAiMemory = memory => api.post('/ai/memories', memory).then(unwrap);
export const updateAiMemory = (id, patch) => api.patch(`/ai/memories/${id}`, patch).then(unwrap);
export const deleteAiMemory = id => api.delete(`/ai/memories/${id}`);

/** Shared chats (CINCHAI-113): a public link up to one message, revocable. */
export const shareAiThread = (id, upToSeq) => api.post(`/ai/threads/${id}/share`, {
  up_to_seq: upToSeq
}).then(unwrap);
export const listAiShares = () => api.get('/ai/shares', {
  params: {
    limit: 200
  }
}).then(unwrap);
export const revokeAiShare = id => api.delete(`/ai/shares/${id}`);
export const cloneSharedAiThread = (token, company) => api.post(`/ai/shared/${token}/clone`, {
  company
}).then(unwrap);

// The public read deliberately skips the shared axios instance: that one adds the signed-in
// user's token, and the public endpoint must be read the same way with or without a session.
export const getSharedAiThread = token => fetch(`${API_URL}/ai/shared/${encodeURIComponent(token)}`, {
  credentials: 'omit'
}).then(r => r.ok ? r.json() : Promise.reject(new Error(r.status === 404 ? 'not_found' : 'unavailable')));

/** Files dropped into a chat (CINCHAI-129): stored on the thread, read by the agent. */
export const uploadAiThreadFile = (threadId, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/ai/threads/${threadId}/files`, form, {
    headers: {
      'content-type': 'multipart/form-data'
    }
  }).then(unwrap);
};

/** Cinch AI automations (CINCHAI-114/115): scheduled prompts that run as their owner. */
export const listAiAutomations = params => api.get('/ai/automations', {
  params: {
    limit: 200,
    ...params
  }
}).then(unwrap);
export const createAiAutomation = automation => api.post('/ai/automations', automation).then(unwrap);
export const updateAiAutomation = (id, patch) => api.patch(`/ai/automations/${id}`, patch).then(unwrap);
export const deleteAiAutomation = id => api.delete(`/ai/automations/${id}`);
export const runAiAutomationNow = id => api.post(`/ai/automations/${id}/run`).then(unwrap);
export const listAiAutomationRuns = (id, params) => api.get(`/ai/automations/${id}/runs`, {
  params: {
    limit: 50,
    ...params
  }
}).then(unwrap);

/** Cinch AI folders (CINCHAI-118): the user's own nested folders; a flat list with `parent`. */
export const listAiFolders = () => api.get('/ai/folders').then(unwrap);
export const createAiFolder = folder => api.post('/ai/folders', folder).then(unwrap);
export const updateAiFolder = (id, patch) => api.patch(`/ai/folders/${id}`, patch).then(unwrap);

/** Deleting keeps the folder's chats (they go back to Chats) unless `deleteChats`. */
export const deleteAiFolder = (id, deleteChats = false) => api.delete(`/ai/folders/${id}`, {
  params: deleteChats ? {
    delete_chats: 'true'
  } : {}
});

/** The user's own Cinch AI usage in a company, with the company's caps and verdict (CINCHAI-121). */
// These two run as the app loads, possibly before the shared client has the token, so the caller
// passes it (a 401 there used to hide the bubble until the next company switch).
const withToken = token => token ? {
  Authorization: `Token ${token}`
} : undefined;
export const getAiUsageMeter = (company, token) => api.get('/ai/usage/meter', {
  params: {
    company
  },
  headers: withToken(token)
}).then(unwrap);

/** The Cinch AI marketplace app (CINCHAI-122): may this user use Cinch AI in the company? */
export const getAiAppAccess = (company, token) => api.get('/ai/app/access', {
  params: {
    company
  },
  headers: withToken(token)
}).then(unwrap);

/** The app's Manage page: company users with their access, the limits, and usage against the caps. */
export const getAiAppSettings = company => api.get('/ai/app/settings', {
  params: {
    company
  }
}).then(unwrap);

/** `users` (the full allow-list, user ids) and/or `limits`. */
export const updateAiAppSettings = (company, body) => api.patch(`/ai/app/settings?company=${company}`, body).then(unwrap);

/** A company's Cinch AI usage per day / user / thread (CINCHAI-120; needs view_billing). */
export const getAiUsageReport = (company, params = {}) => api.get('/ai/usage/report', {
  params: {
    company,
    ...params
  }
}).then(unwrap);

/** Thumbs on a reply (design review, CINCHAI-108): 'up', 'down' or '' to clear. */
// Thumbs on a reply (CINCHAI-108), stored in ai_message_feedback for the daily review; "" clears.
export const rateAiMessage = (messageId, rating, comment) => api.post(`/ai/messages/${messageId}/feedback`, {
  rating,
  ...(comment ? {
    comment
  } : {})
}).then(unwrap);

// A question card is answered once (design review 2, final pass B): kept on the reply, so it stays
// answered after a reload. A second answer is a 409 carrying the first.
export const answerAiCard = (threadId, replyId, card, answer) => api.post(`/ai/threads/${threadId}/messages/${replyId}/answer`, {
  card,
  answer
}).then(unwrap);

// Regenerate (CINCHAI-124): which reply to a question is shown and continued from.
export const setAiActiveReply = (threadId, questionId, childId) => api.post(`/ai/threads/${threadId}/messages/${questionId}/active`, {
  child: childId
}).then(unwrap);